import React, { useState, useEffect, useRef } from "react";
import { loadFiservSDK } from "./fiservProvider";

/*
 * ============================================================
 * THE WHOLE FISERV FLOW, IN ONE PLACE, TOP TO BOTTOM:
 *
 * ON MOUNT (setupFiservFields, called once):
 *   1. Load the SDK script
 *   2. window.fiserv.init(...)          — authenticate the SDK
 *   3. window.fiserv.components.paymentFields(...) — mount the
 *      card number / expiration / security code fields into the
 *      divs already in this component's JSX
 *
 * ON PAY CLICK (handleSubmit):
 *   4. paymentFields.submit()           — tokenize the card
 *   5. window.fiserv.components.threeDSecure() — run 3DS, returns
 *      { transactionState, authenticationTransactionId } — abort
 *      if transactionState === "DECLINED"
 *   6. call onSuccess(...) with authenticationTransactionId
 *
 * That's it — no other files, no other functions to trace.
 * ============================================================
 */

const FieldWrapper = ({ label, mountId, error, leadingIcon }) => (
  <div className="flex flex-col gap-1.5 flex-1">
    <label className="text-[11px] font-bold uppercase tracking-widest text-navy/60 font-heading">
      {label}
    </label>
    <div
      className={`flex items-center gap-2 border rounded-xl px-3.5 py-3 bg-white transition-all duration-150 h-[48px]
        ${
          error
            ? "border-red-400 shadow-[0_0_0_3px_rgba(229,62,62,0.08)]"
            : "border-navy/50 focus-within:border-gold focus-within:shadow-[0_0_0_3px_rgba(212,175,55,0.12)]"
        }`}
    >
      {/* Fiserv's SDK mounts its iframe into this div */}
      <div id={mountId} className="flex-1 h-full" />
    </div>
    {error && <p className="text-[12px] text-red-500 leading-snug">{error}</p>}
  </div>
);

/* Small brand icons, shown beside the card number field once
   onCardBrandChange identifies the brand. SDK values per Fiserv's
   Card Brand table: visa, mastercard, american-express, jcb, etc. */
const BrandIcon = ({ brand }) => {
  if (!brand) return null;

  const icons = {
    visa: (
      <svg width="26" height="17" viewBox="0 0 32 20" fill="none">
        <rect width="32" height="20" rx="3" fill="#1A1F71" />
        <text x="4" y="14" fontFamily="Arial" fontSize="9" fontWeight="bold" fill="white">VISA</text>
      </svg>
    ),
    mastercard: (
      <svg width="26" height="17" viewBox="0 0 32 20" fill="none">
        <rect width="32" height="20" rx="3" fill="#252525" />
        <circle cx="13" cy="10" r="6" fill="#EB001B" />
        <circle cx="19" cy="10" r="6" fill="#F79E1B" />
        <path d="M16 5.8a6 6 0 010 8.4A6 6 0 0116 5.8z" fill="#FF5F00" />
      </svg>
    ),
    "american-express": (
      <svg width="26" height="17" viewBox="0 0 32 20" fill="none">
        <rect width="32" height="20" rx="3" fill="#2E77BC" />
        <text x="3" y="14" fontFamily="Arial" fontSize="7" fontWeight="bold" fill="white">AMEX</text>
      </svg>
    ),
    jcb: (
      <svg width="26" height="17" viewBox="0 0 32 20" fill="none">
        <rect width="32" height="20" rx="3" fill="#003087" />
        <text x="5" y="14" fontFamily="Arial" fontSize="8" fontWeight="bold" fill="white">JCB</text>
      </svg>
    ),
  };

  return icons[brand] || null;
};

export const FiservPaymentForm = ({
  accessToken,
  sessionId,
  config,
  amount,
  currency,
  onSuccess,
  onCancel,
}) => {
  const [ready, setReady] = useState(false);
  const [initError, setInitError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState(null);
  // Real-time validation state, driven by onFieldValidityChange /
  // onFormValid / onFormNoLongerValid hooks (confirmed names from
  // the "Implementing event handling for Hosted Fields" doc).
  const [fieldErrors, setFieldErrors] = useState({});
  const [formValid, setFormValid] = useState(false);
  // Detected card brand, from onCardBrandChange. Fiserv gives lowercase
  // SDK values like "visa", "mastercard", "american-express" (per the
  // Card Brand table in their docs) — null means not yet identified.
  const [cardBrand, setCardBrand] = useState(null);

  const paymentFieldsRef = useRef(null);
  const didSetupRef = useRef(false);

  // Human-readable labels for the BAD_FORM_DATA error array and for
  // fieldState.field values, since Fiserv's field names (cardNumber,
  // expiration, securityCode) aren't what a user should see.
  const FIELD_LABELS = {
    cardNumber: "card number",
    expiration: "expiration date",
    securityCode: "security code",
  };

  const formatAmount = (val) =>
    Number(val).toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

  /* ---------------------------------------------------------- */
  /* STEP 1-3 — runs once when the form appears                 */
  /* ---------------------------------------------------------- */
  useEffect(() => {
    if (didSetupRef.current) return;
    didSetupRef.current = true;

    const setupFiservFields = async () => {
      try {
        // 1. Load the SDK script
        await loadFiservSDK(config.sdk_url);

        if (!window.fiserv?.init) {
          throw new Error("Fiserv SDK failed to load properly");
        }

        // Diagnostic: confirm which environment we're actually
        // initializing with. Fiserv's own docs example used "CERT" —
        // if this logs something else (or undefined), that could be
        // why valid test cards are being rejected as invalid.
        // console.log("Fiserv config.environment:", config.environment);
        // console.log("Fiserv full config:", config);

        const initResponse =  await window.fiserv.init({
          environment: config.environment,
          accessToken,
          apiKey: config.apiKey,
          merchantId: config.merchantId,
          terminalId: config.terminalId,
        });
        // console.log("Fiserv init() response:", JSON.stringify(initResponse, null, 2));
        // 3. Mount the card fields (confirmed field schema from Fiserv docs)
        const paymentFields = await window.fiserv.components.paymentFields({
          data: {
            paymentMethod: "CREDIT_CARD",
            fields: {
              cardNumber: {
                parentElementId: "fiserv-card-number",
                placeholder: "1234 1234 1234 1234",
                enableFormatting: true,
              },
              expiration: {
                parentElementId: "fiserv-expiration",
                placeholder: "MM / YY",
                format: "FLEXIBLE",
                delimiter: " / ",
              },
              securityCode: {
                parentElementId: "fiserv-security-code",
                placeholder: "CVV",
              },
            },
            contextualCssClassNames: {
              valid: "fiserv-field-valid",
              invalid: "fiserv-field-invalid",
            },
          },
          hooks: {
            // Fiserv calls this every time the user types in a field.
            // Real signature: (fieldState, formState, form) — we only
            // need fieldState, but formState is there if useful later
            // (e.g. it may show more detail on why cardNumber fails).
            onFieldValidityChange: (fieldState, formState) => {
              // console.log("Fiserv fieldState:", fieldState);
              // console.log("Fiserv formState:", formState);

              const fieldName = fieldState.field; // "cardNumber", "expiration", or "securityCode"
              const label = FIELD_LABELS[fieldName] || fieldName;

              // NOTE: must use the (prevErrors) => ... form here, not
              // fieldErrors directly. This hook is created once when
              // the form first mounts and never re-created, so a plain
              // fieldErrors reference would always point at the very
              // first, empty {} state — never the real current one.
              setFieldErrors((prevErrors) => {
                const updatedErrors = { ...prevErrors };

                if (fieldState.isValid) {
                  // Field is valid — remove any error message for it
                  delete updatedErrors[fieldName];
                } else if (fieldState.shouldShowError) {
                  // Field is invalid AND Fiserv says it's time to show
                  // the error (not just "still typing")
                  updatedErrors[fieldName] = `Please check your ${label}.`;
                }
                // else: invalid but still typing, don't show an error yet

                return updatedErrors;
              });
            },

            // Fiserv calls this when every field is valid — safe to submit
            // Real signature: (formState, form)
            onFormValid: (formState) => {
              // console.log("Fiserv formState (valid):", formState);
              setFormValid(true);
            },

            // Fiserv calls this if a field goes from valid back to invalid
            // Real signature: (formState, form)
            onFormNoLongerValid: (formState) => {
              // console.log("Fiserv formState (no longer valid):", formState);
              setFormValid(false);
            },

            // Fiserv calls this when the card brand is identified (or
            // no longer identified, e.g. user deletes digits). Real
            // signature: (cardBrand, formState, form) — cardBrand is
            // null if no longer identified.
            onCardBrandChange: (cardBrand) => {
              // console.log("Fiserv cardBrand:", cardBrand);
              setCardBrand(cardBrand);
            },
          },
        });

        paymentFieldsRef.current = paymentFields;
        setReady(true);
      } catch (err) {
        console.error("Fiserv setup failed:", err);
        setInitError(
          "We couldn't load the secure payment form. Please refresh and try again.",
        );
      }
    };

    setupFiservFields();
  }, [accessToken, config]);

  /* ---------------------------------------------------------- */
  /* STEP 4-6 — runs when the user clicks Pay                   */
  /* ---------------------------------------------------------- */
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!paymentFieldsRef.current || submitting) return;

    setSubmitting(true);
    setErrorMsg(null);

    try {
      // Step 4: Collect payment method (tokenize the card)
      const submitResult = await paymentFieldsRef.current.submit();
      // console.log("Fiserv submitResult (raw):", JSON.stringify(submitResult, null, 2));

      let transactionState;
      let authenticationTransactionId;

      // Step 5: Initialize 3DS — only when this reservation/merchant config
      // actually requires it 
      // result — is what avoids the challenge/OTP UI when 3DS is disabled.
      if (config.requires3DS) {
        const threeDSResult = await window.fiserv.components.threeDSecure();
        transactionState = threeDSResult.transactionState;
        authenticationTransactionId = threeDSResult.authenticationTransactionId;
        // console.log("Fiserv threeDSecure() result:", threeDSResult);

        if (transactionState === "DECLINED") {
          setErrorMsg("3D Secure authentication failed. Please try a different card.");
          setSubmitting(false);
          return;
        }
      }

      // Step 6: Submit the Charges API request, referencing the 3DS result.
      // NOTE: I'm not certain whether this Charges API call happens directly
      // from the frontend SDK or needs to go through your BFF — check the
      // "Step 6: Submit a transaction request" section of the same doc page.
      onSuccess({
        success: true,
        sessionId: sessionId,
        transactionState,
        authenticationTransactionId,
        transactionId: submitResult?.gatewayResponse?.transactionProcessingDetails?.transactionId,
      });
    } catch (err) {
      console.error("Fiserv payment error:", err);
      if (err?.message === "BAD_FORM_DATA" && Array.isArray(err?.errors)) {
        const labels = err.errors.map((f) => FIELD_LABELS[f] || f);
        setErrorMsg(`Please check the following: ${labels.join(", ")}.`);
      } else if (err?.message === "BAD_SUBMIT_CONFIG" && Array.isArray(err?.errors)) {
        setErrorMsg(err.errors.join(" "));
      } else {
        setErrorMsg(err?.message || "Payment failed. Please try again.");
      }
      setSubmitting(false);
    }
  };

  return (
    <div className="mt-5 font-body">
      <div className="flex items-center gap-3 mb-5">
        <div className="flex-1 h-px bg-navy/10" />
        <span className="text-[11px] font-extrabold uppercase tracking-[0.15em] text-navy/100 font-heading whitespace-nowrap">
          Payment details
        </span>
        <div className="flex-1 h-px bg-navy/10" />
      </div>

      {initError && (
        <div className="flex items-start gap-2.5 bg-red-50 border border-red-200 rounded-xl px-4 py-3 mb-4">
          <span className="text-[13px] text-red-600 leading-relaxed">
            {initError}
          </span>
        </div>
      )}

      <div className="relative">
        {!ready && !initError && (
          <div className="absolute inset-0 z-10 flex items-center justify-center gap-3 bg-white/80 rounded-xl">
            <span className="w-5 h-5 border-2 border-navy/10 border-t-primary rounded-full animate-spin shrink-0" />
            <span className="text-sm text-navy/40 font-body">
              Preparing secure payment...
            </span>
          </div>
        )}

        <form
          onSubmit={handleSubmit}
          className={`flex flex-col transition-opacity duration-150 ${
            ready ? "" : "opacity-40 pointer-events-none"
          }`}
        >
          <div className="flex items-center justify-between bg-primary/90 rounded-xl px-4 py-3.5 mb-5 gap-3">
            <div className="flex items-center gap-2.5 shrink-0">
              <div className="w-6 h-6 sm:w-8 sm:h-8 rounded-lg bg-white flex items-center justify-center shrink-0">
                <svg width="10" height="12" viewBox="0 0 12 14" fill="none" aria-hidden="true">
                  <path
                    d="M9.5 6V4.5a3.5 3.5 0 10-7 0V6H1v7a1 1 0 001 1h8a1 1 0 001-1V6H9.5zm-6-1.5a2.5 2.5 0 015 0V6h-5V4.5z"
                    fill="#22c55e"
                  />
                </svg>
              </div>
              <span className="text-white text-xs sm:text-sm font-body font-normal whitespace-nowrap">
                Amount to pay now
              </span>
            </div>
            <span className="text-white font-heading font-bold text-sm sm:text-lg tracking-wide text-right whitespace-nowrap">
              {currency.toUpperCase()} {formatAmount(amount)}
            </span>
          </div>

          <div className="flex items-center justify-between mb-5">
            <span className="text-[11px] font-bold text-navy/60 font-body uppercase tracking-widest">
              Accepted cards
            </span>
            <div className="flex items-center gap-1.5">
              {/* Fiserv's SDK values (from their Card Brand table):
                  visa, mastercard, american-express, diners-club,
                  discover, jcb, unionpay, maeestro (their typo, not ours) */}
              <div className={`rounded overflow-hidden ${cardBrand && cardBrand !== "visa" ? "opacity-40" : "opacity-80"}`}>
                <svg width="32" height="20" viewBox="0 0 32 20" fill="none">
                  <rect width="32" height="20" rx="3" fill="#1A1F71" />
                  <text x="4" y="14" fontFamily="Arial" fontSize="9" fontWeight="bold" fill="white">VISA</text>
                </svg>
              </div>
              <div className={`rounded overflow-hidden ${cardBrand && cardBrand !== "mastercard" ? "opacity-40" : "opacity-80"}`}>
                <svg width="32" height="20" viewBox="0 0 32 20" fill="none">
                  <rect width="32" height="20" rx="3" fill="#252525" />
                  <circle cx="13" cy="10" r="6" fill="#EB001B" />
                  <circle cx="19" cy="10" r="6" fill="#F79E1B" />
                  <path d="M16 5.8a6 6 0 010 8.4A6 6 0 0116 5.8z" fill="#FF5F00" />
                </svg>
              </div>
              <div className={`rounded overflow-hidden ${cardBrand && cardBrand !== "american-express" ? "opacity-40" : "opacity-80"}`}>
                <svg width="32" height="20" viewBox="0 0 32 20" fill="none">
                  <rect width="32" height="20" rx="3" fill="#2E77BC" />
                  <text x="3" y="14" fontFamily="Arial" fontSize="7" fontWeight="bold" fill="white">AMEX</text>
                </svg>
              </div>
              <div className={`rounded overflow-hidden ${cardBrand && cardBrand !== "jcb" ? "opacity-40" : "opacity-80"}`}>
                <svg width="32" height="20" viewBox="0 0 32 20" fill="none">
                  <rect width="32" height="20" rx="3" fill="#003087" />
                  <text x="5" y="14" fontFamily="Arial" fontSize="8" fontWeight="bold" fill="white">JCB</text>
                </svg>
              </div>
            </div>
          </div>

          <div className="mb-3.5">
            <FieldWrapper
              label="Card number"
              mountId="fiserv-card-number"
              error={fieldErrors.cardNumber}
              leadingIcon={<BrandIcon brand={cardBrand} />}
            />
          </div>

          <div className="grid grid-cols-2 gap-3 mb-3.5">
            <FieldWrapper
              label="Expiration date"
              mountId="fiserv-expiration"
              error={fieldErrors.expiration}
            />
            <FieldWrapper
              label="Security code"
              mountId="fiserv-security-code"
              error={fieldErrors.securityCode}
            />
          </div>

          {errorMsg && (
            <div className="flex items-start gap-2.5 bg-red-50 border border-red-200 rounded-xl px-4 py-3 mb-4">
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true" className="shrink-0 mt-0.5">
                <circle cx="8" cy="8" r="7.5" stroke="#e53e3e" strokeWidth="1" />
                <path d="M8 5v3.5M8 10.5v.5" stroke="#e53e3e" strokeWidth="1.5" strokeLinecap="round" />
              </svg>
              <span className="text-[13px] text-red-600 leading-relaxed">
                {errorMsg}
              </span>
            </div>
          )}

          <div className="flex flex-col sm:flex-row gap-2.5 mt-1 mb-3.5">
            <button
              type="submit"
              disabled={!ready || submitting || Object.keys(fieldErrors).length > 0}
              className={`flex-1 w-full flex items-center justify-center gap-2
                          py-3.5 rounded-xl text-sm font-bold font-heading
                          tracking-wide text-white transition-all duration-150
                          ${
                            ready && !submitting && Object.keys(fieldErrors).length === 0
                              ? "bg-primary hover:bg-primary-hover shadow-primary-glow active:scale-[0.98] cursor-pointer"
                              : "bg-primary/100 cursor-not-allowed"
                          }`}
            >
              {submitting ? (
                <>
                  <span className="w-4 h-4 border-2 border-primary border-t-primary rounded-full animate-spin" />
                  Processing...
                </>
              ) : (
                <>
                  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
                    <path
                      d="M11 4.5H3a1 1 0 00-1 1V10a1 1 0 001 1h8a1 1 0 001-1V5.5a1 1 0 00-1-1zM3 4.5V4a4 4 0 118 0v.5"
                      stroke="rgb(255, 255, 255)"
                      strokeWidth="1.2"
                      strokeLinecap="round"
                    />
                  </svg>
                  Pay {currency.toUpperCase()} {formatAmount(amount)}
                </>
              )}
            </button>
            <button
              type="button"
              onClick={onCancel}
              disabled={submitting}
              className="flex-1 py-3.5 bg-white border border-navy/50 rounded-xl
                 text-sm font-semibold uppercase tracking-widest text-navy/60 font-heading
                 hover:bg-red-50 hover:border-red-300 hover:text-red-500
                 transition-all duration-150 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Cancel
            </button>
          </div>

          <div className="flex items-center justify-center gap-1.5 pt-1">
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true">
              <path d="M6 1a5 5 0 100 10A5 5 0 006 1zm0 9A4 4 0 116 2a4 4 0 010 8z" fill="#94a3b8" />
              <path d="M6 5.5a.5.5 0 000 1h.5a.5.5 0 000-1H6zM5.5 4a.5.5 0 011 0v.5a.5.5 0 01-1 0V4z" fill="#94a3b8" />
            </svg>
            <span className="text-[11px] text-navy/30 font-body">
              Your payment is encrypted and secured by{" "}
              <strong className="font-semibold text-navy/40">Fiserv</strong>
            </span>
          </div>
        </form>
      </div>
    </div>
  );
};